import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, ScanText, Upload } from "lucide-react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useServerFn } from "@tanstack/react-start";
import { attachExperienceProof, listExperienceIndex } from "@/lib/parsed-experiences.functions";

export const Route = createFileRoute("/admin/upload-experience")({
  head: () => ({
    meta: [
      { title: "Upload Interview Experience - Admin" },
      { name: "description", content: "Upload WhatsApp interview proofs and auto-extract details with on-device OCR." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: UploadExperiencePage,
});

const CONSULATES = ["Mumbai", "Hyderabad", "New Delhi", "Chennai", "Kolkata"];
const CATEGORIES = ["F-1 Student", "214(b) Refusal Approved", "B-1/B-2", "H-1B"];
const MENTOR_KEYWORDS = [
  "harsha",
  "srinath",
  "mock",
  "mocks",
  "welivdit mock",
  "mock session",
  "mock interview",
  "enrolled",
  "paid plan",
];

const MONTHS = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];

/** Best-effort date parse from OCR text: "24th July 2026", "08/06/2026", "4 May 2026". */
function extractDate(text: string): string {
  const t = text.toLowerCase();
  const dmy = t.match(/(\d{1,2})(?:st|nd|rd|th)?\s+([a-z]{3,9})\.?\s*(\d{4})?/);
  if (dmy) {
    const monthIdx = MONTHS.findIndex((m) => m.startsWith(dmy[2]!.slice(0, 3)));
    if (monthIdx >= 0) {
      const year = dmy[3] ?? String(new Date().getFullYear());
      const day = dmy[1]!.padStart(2, "0");
      return `${year}-${String(monthIdx + 1).padStart(2, "0")}-${day}`;
    }
  }
  const numeric = t.match(/(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (numeric) {
    return `${numeric[3]}-${numeric[2]!.padStart(2, "0")}-${numeric[1]!.padStart(2, "0")}`;
  }
  return "";
}

function extractUniversity(text: string): string {
  const m = text.match(/(?:university|uni|college)\s*[:\-]\s*([A-Za-z0-9 .,&'-]{3,60})/i);
  if (m) return m[1]!.split("\n")[0]!.trim();
  const known = [
    "Georgia Tech",
    "Carnegie Mellon",
    "Northeastern",
    "UMass Amherst",
    "Tufts University",
    "UIUC",
    "USC",
    "ASU",
    "NYU",
    "Purdue",
    "Columbia",
  ];
  return known.find((k) => text.toLowerCase().includes(k.toLowerCase())) ?? "";
}

/** Best-effort person name from a WhatsApp screenshot transcript. */
function extractDisplayName(text: string): string {
  const labelled = text.match(/(?:name|from)\s*[:\-]\s*([A-Za-z][A-Za-z .']{2,40})/i);
  if (labelled) return labelled[1]!.trim();
  const lines = text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  const candidate = lines.find(
    (l) =>
      /^[A-Z][a-z]+(\s+[A-Z][a-z]+){0,2}$/.test(l) &&
      l.length <= 32 &&
      !/(visa|consulate|university|interview|approved|mumbai|delhi|chennai|kolkata|hyderabad)/i.test(l),
  );
  return candidate ?? "";
}


function UploadExperiencePage() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [ocrText, setOcrText] = useState("");
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const objectUrlRef = useRef<string | null>(null);

  const [displayName, setDisplayName] = useState("Verified Community Member");
  const [consulate, setConsulate] = useState("Mumbai");
  const [category, setCategory] = useState("F-1 Student");
  const [interviewDate, setInterviewDate] = useState("");
  const [university, setUniversity] = useState("");
  const [summary, setSummary] = useState("");
  const [isScholar, setIsScholar] = useState(false);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  const onSelect = async (f: File | null) => {
    if (!f) return;
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    const url = URL.createObjectURL(f);
    objectUrlRef.current = url;
    setFile(f);
    setPreview(url);
    await runOcr(f);
  };

  const runOcr = async (f: File) => {
    setScanning(true);
    setProgress(0);
    try {
      // Lazy-loaded, browser-only OCR — runs entirely on the admin's device.
      const { default: Tesseract } = await import("tesseract.js");
      const result = await Tesseract.recognize(f, "eng", {
        logger: (m: { status: string; progress: number }) => {
          if (m.status === "recognizing text") setProgress(Math.round(m.progress * 100));
        },
      });
      const text = result.data.text ?? "";
      setOcrText(text);
      autofill(text);
      toast.success("Text extracted on-device");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "OCR failed");
    } finally {
      setScanning(false);
    }
  };

  const autofill = (text: string) => {
    const lower = text.toLowerCase();
    const foundConsulate = CONSULATES.find((c) => lower.includes(c.toLowerCase()))
      ?? (lower.includes("delhi") ? "New Delhi" : undefined);
    if (foundConsulate) setConsulate(foundConsulate);

    if (lower.includes("214b") || lower.includes("214(b)")) setCategory("214(b) Refusal Approved");
    else if (lower.includes("b1/b2") || lower.includes("b-1/b-2")) setCategory("B-1/B-2");
    else if (lower.includes("h1b") || lower.includes("h-1b")) setCategory("H-1B");
    else if (lower.includes("f1") || lower.includes("f-1")) setCategory("F-1 Student");

    const d = extractDate(text);
    if (d) setInterviewDate(d);

    const uni = extractUniversity(text);
    if (uni) setUniversity(uni);

    const who = extractDisplayName(text);
    if (who) setDisplayName(who);

    const lines = text
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 25);
    setSummary(lines.slice(0, 2).join(" ").slice(0, 400));

    setIsScholar(MENTOR_KEYWORDS.some((k) => lower.includes(k)));

  };

  const save = async () => {
    if (!file) return toast.error("Choose a screenshot first");
    setSaving(true);
    try {
      const ext = file.name.split(".").pop() || "jpg";
      const path = `admin-upload-${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("experiences")
        .upload(path, file, { contentType: file.type, upsert: false });
      if (upErr) throw upErr;

      const { error: insErr } = await supabase.from("interview_experiences" as never).insert({
        display_name: displayName || "Verified Community Member",
        consulate_location: consulate,
        visa_category: category,
        interview_date: interviewDate || null,
        screenshot_url: path,
        has_proof_badge: true,
        summary_text: summary || null,
        university_name: university || null,
        is_enrolled_member: isScholar,
      } as never);
      if (insErr) throw insErr;

      toast.success("Experience published");
      setFile(null);
      setPreview(null);
      setOcrText("");
      setSummary("");
      setUniversity("");
      setInterviewDate("");
      setIsScholar(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to publish experience");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminShell
      title="Upload Interview Experience"
      description="On-device OCR auto-fills the fields — no AI credits used."
    >
      <ProofMatcher
        onPrefill={(entry) => {
          setDisplayName(entry.studentName);
          setConsulate(entry.consulate);
          setInterviewDate(entry.date);
          setSummary(entry.transcript.slice(0, 400));
          setIsScholar(entry.isMentored);
        }}
      />

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="space-y-4 rounded-xl border border-border bg-card p-5">
          <div>
            <Label htmlFor="exp-file">WhatsApp screenshot</Label>
            <Input
              id="exp-file"
              type="file"
              accept="image/*"
              className="mt-2"
              onChange={(e) => void onSelect(e.target.files?.[0] ?? null)}
            />
          </div>

          {scanning && (
            <div className="space-y-2">
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Extracting details from screenshot… {progress}%
              </p>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-300"
                  style={{ width: `${Math.max(6, progress)}%` }}
                />
              </div>
            </div>
          )}


          {preview && (
            <div className="max-h-96 overflow-y-auto rounded-lg border border-border">
              <img src={preview} alt="Selected screenshot preview" className="w-full" />
            </div>
          )}

          {ocrText && (
            <div>
              <Label htmlFor="ocr-text" className="flex items-center gap-1.5">
                <ScanText className="h-3.5 w-3.5" /> Extracted text
              </Label>
              <Textarea id="ocr-text" value={ocrText} readOnly rows={6} className="mt-2 text-xs" />
            </div>
          )}
        </div>

        <div className="space-y-4 rounded-xl border border-border bg-card p-5">
          <div>
            <Label htmlFor="exp-name">Display name</Label>
            <Input id="exp-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="mt-2" />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Consulate</Label>
              <Select value={consulate} onValueChange={setConsulate}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONSULATES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Visa category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="exp-date">Interview date</Label>
              <Input
                id="exp-date"
                type="date"
                value={interviewDate}
                onChange={(e) => setInterviewDate(e.target.value)}
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="exp-uni">University</Label>
              <Input id="exp-uni" value={university} onChange={(e) => setUniversity(e.target.value)} className="mt-2" />
            </div>
          </div>

          <div>
            <Label htmlFor="exp-summary">2-line summary</Label>
            <Textarea
              id="exp-summary"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={4}
              className="mt-2"
            />
          </div>

          <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
            <div>
              <p className="text-sm font-medium text-foreground">WeLivdIt Mentored Scholar</p>
              <p className="text-xs text-muted-foreground">Auto-detected from mock / mentor keywords</p>
            </div>
            <Switch checked={isScholar} onCheckedChange={setIsScholar} />
          </div>

          <Button onClick={() => void save()} disabled={saving || scanning || !file} className="w-full">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
            Publish experience
          </Button>
        </div>
      </div>
    </AdminShell>
  );
}

type IndexEntry = {
  serialNumber: string;
  studentName: string;
  consulate: string;
  date: string;
  outcome: string;
  university: string | null;
  isMentored: boolean;
  transcript: string;
  hasProof: boolean;
};

/**
 * Proof matching system: pick a parsed WhatsApp experience by serial number,
 * then drag-and-drop the matching chat screenshot to bind it to that entry.
 */
function ProofMatcher({ onPrefill }: { onPrefill: (entry: IndexEntry) => void }) {
  const [entries, setEntries] = useState<IndexEntry[]>([]);
  const [selected, setSelected] = useState<string>("");
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [linked, setLinked] = useState<{ serial: string; name: string } | null>(null);

  const loadIndex = useServerFn(listExperienceIndex);
  const attach = useServerFn(attachExperienceProof);

  useEffect(() => {
    void (async () => {
      try {
        setEntries((await loadIndex()) as IndexEntry[]);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to load parsed experiences");
      }
    })();
  }, [loadIndex]);

  const current = entries.find((e) => e.serialNumber === selected) ?? null;

  const onPick = (serial: string) => {
    setSelected(serial);
    setLinked(null);
    const entry = entries.find((e) => e.serialNumber === serial);
    if (entry) onPrefill(entry);
  };

  const handleFile = async (f: File | null) => {
    if (!f) return;
    if (!current) return toast.error("Select a serial number first");
    if (!f.type.startsWith("image/")) return toast.error("Only image files are supported");
    setUploading(true);
    try {
      const ext = f.name.split(".").pop() || "jpg";
      const path = `proof-${current.serialNumber.replace("#", "")}-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("experiences")
        .upload(path, f, { contentType: f.type, upsert: false });
      if (upErr) throw upErr;

      const res = (await attach({ data: { serialNumber: current.serialNumber, screenshotPath: path } })) as {
        serialNumber: string;
        studentName: string;
      };
      setLinked({ serial: res.serialNumber, name: res.studentName });
      setEntries((prev) =>
        prev.map((e) => (e.serialNumber === current.serialNumber ? { ...e, hasProof: true } : e)),
      );
      toast.success(`Proof linked to ${res.serialNumber}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to attach proof");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4 rounded-xl border border-border bg-card p-5">
      <div>
        <Label>Select Student / Serial Number to Attach Proof</Label>
        <Select value={selected} onValueChange={onPick}>
          <SelectTrigger className="mt-2">
            <SelectValue placeholder={entries.length ? "Choose a parsed experience" : "Loading experiences…"} />
          </SelectTrigger>
          <SelectContent className="max-h-80">
            {entries.map((e) => (
              <SelectItem key={e.serialNumber} value={e.serialNumber}>
                {e.serialNumber} | {e.studentName} | {e.consulate} |{" "}
                {e.isMentored ? "🔥 Mentored Scholar" : "👥 General Community"}{" "}
                {e.hasProof ? "(Proof Attached)" : "(No Proof Attached)"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {current && (
        <p className="rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
          {current.outcome} · {current.date} {current.university ? `· ${current.university}` : ""}
          <br />
          {current.transcript.slice(0, 220)}…
        </p>
      )}

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          void handleFile(e.dataTransfer.files?.[0] ?? null);
        }}
        className={`rounded-xl border-2 border-dashed p-6 text-center transition ${
          dragging ? "border-primary bg-primary/5" : "border-border"
        }`}
      >
        <p className="text-sm font-medium text-foreground">Upload WhatsApp Proof Screenshot</p>
        <p className="mt-1 text-xs text-muted-foreground">Drag &amp; drop an image here, or choose a file below.</p>
        <Input
          type="file"
          accept="image/*"
          aria-label="Upload WhatsApp proof screenshot"
          className="mx-auto mt-3 max-w-xs"
          disabled={!current || uploading}
          onChange={(e) => void handleFile(e.target.files?.[0] ?? null)}
        />
        {uploading && (
          <p className="mt-3 flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading proof…
          </p>
        )}
      </div>

      {linked && (
        <p className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm font-semibold text-emerald-600">
          ✓ Proof Screenshot linked to {linked.serial} ({linked.name})
        </p>
      )}
    </div>
  );
}
